import JSZip from "jszip";

import {
  parsePreslinkTagMainFields,
  type PreslinkTagMainFields,
} from "../../../services/ai/preslinkWorkbook";

/** The Path that library slides carry until they are linked to a deck folder. */
export const PRESLINK_ROOT_PLACEHOLDER = "{FINSTER_PRESLINK_ROOT}";

const TAG_PART_PATTERN = /^ppt\/tags\/[^/]+\.xml$/i;
const TAG_ELEMENT_PATTERN = /<(?:[\w.-]+:)?tag\b([^>]*?)\/?>/g;
const PRESLINK_APPLICATION_PATTERN = /\bapplication\s*=\s*["']PresLink["']/i;

export interface PptxPreslinkTags {
  /** One entry per PresLink `DMSOFTTAG` in the file. */
  tags: PreslinkTagMainFields[];
  hasRootPlaceholder: boolean;
}

/** FactSet writes other applications' data into `DMSOFTTAG` too; only PresLink's are workbook links. */
export function isPreslinkDmsoftTag(value: string): boolean {
  return PRESLINK_APPLICATION_PATTERN.test(value);
}

function decodeXmlText(value: string): string {
  return value.replace(/&(#x[0-9a-f]+|#\d+|quot|apos|lt|gt|amp);/gi, (entity, body: string) => {
    const lower = body.toLowerCase();
    if (lower.startsWith("#x")) return String.fromCodePoint(Number.parseInt(lower.slice(2), 16));
    if (lower.startsWith("#")) return String.fromCodePoint(Number.parseInt(lower.slice(1), 10));
    return { quot: '"', apos: "'", lt: "<", gt: ">", amp: "&" }[lower] ?? entity;
  });
}

function readXmlAttribute(attributes: string, name: string): string | undefined {
  const match = new RegExp(`(?:^|\\s)${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)')`).exec(attributes);
  return match ? decodeXmlText(match[1] ?? match[2] ?? "") : undefined;
}

/**
 * Read the PresLink link tags stored in a PPTX file's `ppt/tags` parts.
 * Throws when a PresLink tag lacks the fields a link needs.
 */
export async function readPreslinkTagsFromPptx(base64: string): Promise<PptxPreslinkTags> {
  const zip = await JSZip.loadAsync(base64, { base64: true });
  const tags: PreslinkTagMainFields[] = [];
  let hasRootPlaceholder = false;
  for (const [partName, entry] of Object.entries(zip.files)) {
    if (entry.dir || !TAG_PART_PATTERN.test(partName)) continue;
    const xml = await entry.async("string");
    if (xml.includes(PRESLINK_ROOT_PLACEHOLDER)) hasRootPlaceholder = true;
    for (const match of xml.matchAll(TAG_ELEMENT_PATTERN)) {
      if (readXmlAttribute(match[1], "name")?.toUpperCase() !== "DMSOFTTAG") continue;
      const value = readXmlAttribute(match[1], "val") ?? "";
      if (!isPreslinkDmsoftTag(value)) continue;
      tags.push(parsePreslinkTagMainFields({ DMSOFTTAG: value }));
    }
  }
  return { tags, hasRootPlaceholder };
}
